import type { HelpStep } from "../../help/types";

/**
 * Steps for the add/edit benefit implementation dialog (`components/ImplementBenefitDialog`),
 * shared by the implementation page and the Company profile page, which both open it.
 *
 * Pure data (only `import type`). Deliberately NOT named `help.<x>.ts`: the help scripts treat
 * that pattern as a page guide, and this module is only a building block spread into the guides.
 *
 * Context keys used: "implementation-dialog", "implementation-dialog-add" and
 * "implementation-dialog-edit" (each guide says how to open the dialog on its page), plus the
 * consent keys in IMPLEMENTATION_CONSENT_CONTEXTS, which read the same everywhere.
 */

export const IMPLEMENTATION_DIALOG_BENEFIT = "Implementation dialog: benefit and consent";
export const IMPLEMENTATION_DIALOG_POLICY = "Implementation dialog: policy and validity";

export const IMPLEMENTATION_CONSENT_CONTEXTS: Record<string, string> = {
  "consent-fields": 'In the implementation dialog, set "Consent Needed" to Yes.',
  "consent-url":
    'In the implementation dialog, set "Consent Needed" to Yes and choose the insurer-URL option under "Insurer Consent Type".',
  "consent-form":
    'In the implementation dialog, set "Consent Needed" to Yes and choose the insurer-form option under "Insurer Consent Type".',
  "consent-text":
    'In the implementation dialog, set "Consent Needed" to Yes and choose the I-agree-text option under "Insurer Consent Type".',
};

export const implementationDialogSteps: HelpStep[] = [
  {
    selector: '[data-help="implementation-dialog-name"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "implementation-dialog",
    title: "Name the implementation",
    body: "A new implementation starts with the category name; make it specific, for example with the insurer or plan. Required, 2 to 128 characters. The server refuses two implementations of the same category with the same name and overlapping validity.",
  },
  {
    selector: '[data-help="implementation-dialog-consent-needed"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "implementation-dialog",
    title: "Decide whether employees must give consent",
    body: 'Yes means employees must accept an insurer consent before they can enroll; the consent fields appear below. Choosing Yes sets "Is Auto Enrollment" to No, and every enrollment into its packages then needs approval.',
  },
  {
    selector: '[data-help="implementation-dialog-auto-enrollment"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "implementation-dialog",
    title: "Set the auto-enrollment default",
    body: 'Yes marks the implementation as auto-enrollment; new packages start with "Is Auto Enrollment" ticked. Disabled while "Consent Needed" is Yes, and choosing Yes here sets "Consent Needed" to No. The server refuses auto-enrollment together with consents.',
  },
  {
    selector: '[data-help="implementation-dialog-currency"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "implementation-dialog",
    title: "Choose the currency",
    body: "The currency of every package price in this implementation. A new implementation starts with the currency of the client's country; type to filter the list. Required.",
  },
  {
    selector: '[data-help="implementation-dialog-consent-type"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "consent-fields",
    title: "Choose how the insurer consent is given",
    body: '"Insurer Consent Type" decides what the employee gets: a link to the insurer\'s own consent page, an insurer form to fill in, or a text to accept ("I agree"). Required when consent is needed; the matching fields appear next to it.',
  },
  {
    selector: '[data-help="implementation-dialog-consent-url"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "consent-url",
    title: "Enter the insurer consent link",
    body: '"Consent Url" is the address of the insurer\'s consent page that employees open during enrollment. Required for this consent type, up to 1024 characters.',
  },
  {
    selector: '[data-help="implementation-dialog-consent-delivery"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "consent-form",
    title: "Choose how the consent form is delivered",
    body: '"Consent Delivery Type" says how the filled-in insurer form reaches the insurer. Required when the consent type is an insurer form.',
  },
  {
    selector: '[data-help="implementation-dialog-consent-document"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "consent-form",
    title: "Upload the insurer consent form",
    body: '"Consent Document" is the form employees download and fill in. Required when the consent type is an insurer form; the server refuses the implementation without it.',
  },
  {
    selector: '[data-help="implementation-dialog-consent-text"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "consent-text",
    title: "Write the consent text",
    body: '"Consent text to be displayed" is the statement employees accept during enrollment. Required for the I-agree consent type.',
  },
  {
    selector: '[data-help="implementation-dialog-description"]',
    section: IMPLEMENTATION_DIALOG_BENEFIT,
    context: "implementation-dialog",
    title: "Describe the implementation",
    body: "What this implementation offers, shown on its card. Required, 2 to 4000 characters.",
  },
  {
    selector: '[data-help="implementation-dialog-policy"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog",
    title: "Pick the policy from GrECo's records",
    body: '"Select Policy" searches the client\'s policies as you type. Choosing one fills in the carrier, the policy number and the validity dates; check them before saving. Optional: you can also type the values yourself.',
  },
  {
    selector: '[data-help="implementation-dialog-insurer"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog",
    title: "Pick the insurer",
    body: '"Select Insurer" searches the insurers of the country as you type and fills in the carrier. Use it when there is no policy to pick, or the policy has a different carrier name.',
  },
  {
    selector: '[data-help="implementation-dialog-carrier"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog",
    title: "Check the carrier",
    body: "The insurer or provider of the benefit, filled in by the policy or insurer search or typed by hand. Required, up to 254 characters. New packages start with this carrier.",
  },
  {
    selector: '[data-help="implementation-dialog-policy-number"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog",
    title: "Enter the policy number or contract ID",
    body: 'Required. The server refuses a second implementation of the same category with the same "Policy Number / Contract ID" and an overlapping validity period.',
  },
  {
    selector: '[data-help="implementation-dialog-valid-from"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog",
    title: "Set the start of validity",
    body: "First day the implementation is valid, typed as day.month.year or picked from the calendar. Required, not earlier than 1 January 2024. Package eligibility dates are never calculated before this date.",
  },
  {
    selector: '[data-help="implementation-dialog-valid-to"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog",
    title: "Set the end of validity",
    body: 'End of the validity period. Required, and must be after "Valid From". The "Only Active" switch on the implementation page shows only implementations valid today.',
  },
  {
    selector: '[data-help="implementation-dialog-attachments"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog-add",
    title: "Attach documents to a new implementation",
    body: 'Click "Upload" to add files that are saved together with the new implementation. The eye icon on each file hides it from employees or shows it; the bin icon drops it before saving.',
  },
  {
    selector: '[data-help="implementation-dialog-documents"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog-edit",
    title: "Manage the documents of an implementation",
    body: 'Shows how many documents the implementation has and opens the documents dialog. Changes made there are saved immediately, not with the "Save" button of this form.',
  },
  {
    selector: '[data-help="implementation-dialog-save"]',
    section: IMPLEMENTATION_DIALOG_POLICY,
    context: "implementation-dialog",
    title: "Save the implementation",
    body: "Checks the required fields, then creates or updates the implementation and closes the dialog. If the server refuses the data (for example an overlapping name or policy number), an error message appears and your input stays.",
  },
];
